/*
  GWP schedule shared by the mini-cart, the BFCM nanobar and the PDP badge, so every timer reads the
  same "GWP disabling time" theme setting and shows the same time.
*/
window.GwpSchedule = {
  // "YYYY-MM-DD HH:MM" in New York time, the same format as the checkout block's "Offer end time".
  // Returns epoch ms, or null when blank or not a real date/time — a typo must never turn the GWP off.
  parseNewYorkTime(value) {
    const text = String(value || '').trim();
    if (!text) return null;

    const newYork = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
    // [year, month, day, hour, minute] shown on a New York clock at the instant ms
    const newYorkFields = (ms) => {
      const parts = {};
      for (const { type, value } of newYork.formatToParts(new Date(ms))) parts[type] = Number(value);
      return [parts.year, parts.month, parts.day, parts.hour, parts.minute];
    };
    const asUtc = ([year, month, day, hour, minute]) => Date.UTC(year, month - 1, day, hour, minute);

    const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(text);
    const wanted = match ? match.slice(1).map(Number) : null;
    let ms = null;
    if (wanted) {
      // New York's UTC offset moves with daylight saving, so it's measured rather
      // than hardcoded; the second pass lands on the right side of a DST change.
      ms = asUtc(wanted);
      for (let pass = 0; pass < 2; pass += 1) ms += asUtc(wanted) - asUtc(newYorkFields(ms));
      // Dates or times that don't exist (Nov 31, 24:00, the hour skipped in spring)
      if (newYorkFields(ms).join() !== wanted.join()) ms = null;
    }
    if (ms === null) {
      console.error(`GWP: ignoring "GWP disabling time" ${JSON.stringify(text)}, expected a real "YYYY-MM-DD HH:MM" in New York time`);
    }
    return ms;
  },
};

/*
  Counts down the flash GWP window. Outside the window it stays idle; inside it writes the minutes and
  seconds left into its [data-gwp-minutes] and [data-gwp-seconds] children. Its state ("off", "active"
  or "last-minute") is set as data-gwp-flash on the closest element that declares that attribute, so
  each surface decides with CSS what to show in every state.
*/
class GwpCountdown extends HTMLElement {
  // The flash GWP runs for the last hour before the disabling time
  static FLASH_WINDOW_MS = 60 * 60 * 1000;
  static LAST_MINUTE_MS = 60 * 1000;
  // setTimeout can't wait longer than ~24.8 days; a longer wait wakes up early and reschedules
  static MAX_TIMEOUT_MS = 2147483647;

  connectedCallback() {
    this.endsAt = window.GwpSchedule.parseNewYorkTime(this.dataset.endsAt);
    this.stateHolder = this.closest('[data-gwp-flash]');
    if (!this.stateHolder) return;
    this.minutesOutput = this.querySelector('[data-gwp-minutes]');
    this.secondsOutput = this.querySelector('[data-gwp-seconds]');
    // Timers stall while a laptop sleeps, so re-sync when the tab comes back
    this.onVisibilityChange = () => {
      if (document.visibilityState === 'visible') this.tick();
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.tick();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  tick() {
    clearTimeout(this.timer);
    if (this.endsAt === null) return;

    const { FLASH_WINDOW_MS, LAST_MINUTE_MS } = GwpCountdown;
    const remaining = this.endsAt - Date.now();

    if (remaining <= 0) {
      this.setState('off');
      return;
    }
    if (remaining > FLASH_WINDOW_MS) {
      this.setState('off');
      this.wakeUpIn(remaining - FLASH_WINDOW_MS);
      return;
    }

    const secondsLeft = Math.ceil(remaining / 1000);
    this.write(this.minutesOutput, String(Math.floor(secondsLeft / 60)));
    this.write(this.secondsOutput, String(secondsLeft % 60).padStart(2, '0'));
    this.setState(remaining <= LAST_MINUTE_MS ? 'last-minute' : 'active');
    // Wakes up right when the displayed second changes
    this.wakeUpIn(remaining - (secondsLeft - 1) * 1000);
  }

  wakeUpIn(milliseconds) {
    this.timer = setTimeout(() => this.tick(), Math.min(milliseconds, GwpCountdown.MAX_TIMEOUT_MS));
  }

  // Minutes change once a minute: skipping identical writes avoids relayouts every second
  write(output, text) {
    if (output.textContent !== text) output.textContent = text;
  }

  setState(state) {
    if (this.stateHolder.dataset.gwpFlash !== state) this.stateHolder.dataset.gwpFlash = state;
  }
}

customElements.define('gwp-countdown', GwpCountdown);
